export type PipelineTaskStatus = 'NOT_STARTED' | 'OPEN' | 'COMPLETED' | 'SKIPPED';

export interface MovableTask {
  id: string;
  key: string;
  status: PipelineTaskStatus;
  dependsOn: string[];
  sortOrder: number;
}

export interface MoveToStepPlan<T> {
  targetKey: string;
  // Unfinished steps above the target, in list order. The user picks which ones the move closes.
  closable: T[];
  // Closable steps the target waits on; they must close for it to open.
  requiredIds: string[];
  // Started or finished steps below the target, in list order. The user picks which ones go
  // back to not started; see forcedResetIds for the ones that must.
  resettable: T[];
  // The target isn't open yet; the move opens it with a fresh due date.
  openTarget: boolean;
}

const isClosed = (status: PipelineTaskStatus) => status === 'COMPLETED' || status === 'SKIPPED';

export function planMoveToStep<T extends MovableTask>(
  tasks: T[],
  targetId: string,
): MoveToStepPlan<T> {
  const target = tasks.find((t) => t.id === targetId);
  if (!target) {
    return {targetKey: '', closable: [], requiredIds: [], resettable: [], openTarget: false};
  }
  const byKey = new Map(tasks.map((t) => [t.key, t]));

  const ancestors = new Set<string>();
  const pending = [...target.dependsOn];
  while (pending.length) {
    const key = pending.pop()!;
    if (ancestors.has(key) || !byKey.has(key)) continue;
    ancestors.add(key);
    pending.push(...byKey.get(key)!.dependsOn);
  }

  const closable = tasks
    .filter((t) => !isClosed(t.status) && (t.sortOrder < target.sortOrder || ancestors.has(t.key)))
    .sort((a, b) => a.sortOrder - b.sortOrder);

  return {
    targetKey: target.key,
    closable,
    requiredIds: closable.filter((t) => ancestors.has(t.key)).map((t) => t.id),
    resettable: tasks
      .filter(
        (t) =>
          !ancestors.has(t.key) &&
          t.id !== target.id &&
          t.sortOrder > target.sortOrder &&
          t.status !== 'NOT_STARTED',
      )
      .sort((a, b) => a.sortOrder - b.sortOrder),
    openTarget: target.status !== 'OPEN',
  };
}

// Resettable steps that can't be kept as they are because they wait on the target or on a
// step that is going back to not started.
export function forcedResetIds(plan: MoveToStepPlan<MovableTask>, resetIds: string[]): string[] {
  const goingBack = new Set([
    plan.targetKey,
    ...plan.resettable.filter((t) => resetIds.includes(t.id)).map((t) => t.key),
  ]);
  const forced: string[] = [];
  let grew = true;
  while (grew) {
    grew = false;
    for (const task of plan.resettable) {
      if (!forced.includes(task.id) && task.dependsOn.some((key) => goingBack.has(key))) {
        forced.push(task.id);
        goingBack.add(task.key);
        grew = true;
      }
    }
  }
  return forced;
}

// Reset steps whose prerequisites are all done after the move reopen right away with a fresh
// due date instead of staying not started.
export function restartingIds<T extends MovableTask>(
  tasks: T[],
  plan: MoveToStepPlan<T>,
  closeIds: string[],
  resetIds: string[],
): string[] {
  const statusAfter = new Map(tasks.map((t) => [t.key, t.status]));
  for (const task of tasks) {
    if (closeIds.includes(task.id)) statusAfter.set(task.key, 'COMPLETED');
    if (resetIds.includes(task.id)) statusAfter.set(task.key, 'NOT_STARTED');
  }
  statusAfter.set(plan.targetKey, 'OPEN');
  // A dependency on a step removed from the group no longer blocks.
  const done = (key: string) => !statusAfter.has(key) || isClosed(statusAfter.get(key)!);
  return tasks.filter((t) => resetIds.includes(t.id) && t.dependsOn.every(done)).map((t) => t.id);
}

export function isEmptyMove(
  plan: MoveToStepPlan<unknown>,
  closeIds: string[],
  resetIds: string[],
): boolean {
  return !closeIds.length && !resetIds.length && !plan.openTarget;
}
